import { Box, Skeleton, Stack } from '@mui/material';
import { intentionallyFloatingPromiseReturn } from 'core/utils/commonUtils';
import { isMobile } from 'core/utils/deviceDetect/selectors';
import useOrderImages, { SWR_ORDER_IMAGES_ENDPOINT } from 'hooks/swr/useOrderImages';
import { isArray, times } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { observer } from 'mobx-react-lite';
import { useId, type FC, useState, useMemo, useContext } from 'react';
import * as orderImageService from 'services/orderImageService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import { useOrderPageStore } from '../store/useOrderPageStore';
import OrderImageList from './ImageListEdit';
import OrderImageLightbox from './OrderImageLightbox';
import UploadButton from './UploadButton';
import UploadDropZone from './UploadDropZone';
import type { OrderImage } from 'models/order/orderImage';
import type { SlideImage } from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

export interface ImageGalleryProps {
  orderId: number;
}

const ImageGallery: FC<ImageGalleryProps> = observer(({ orderId }) => {
  const [index, setIndex] = useState(-1);

  const { data, isLoading } = useOrderImages(orderId);

  const componentId = useId();

  const orderPageStore = useOrderPageStore();
  const { notifyStore } = useContext(StoreContext);

  const imageDeleteConfirm = useConfirm();

  const slides: SlideImage[] = useMemo(
    () =>
      data?.map<SlideImage>((m) => ({
        src: m.url,
        alt: m.name,
        type: 'image',
        share: { url: m.url, title: m.name },
        download: { url: m.url, filename: m.name },
      })) ?? [],
    [data]
  );

  const deleteImage = async (item: OrderImage): Promise<void> => {
    await imageDeleteConfirm({
      description: `Удалить "${item.name ?? ''}"?`,
    });

    const newImages = data?.filter((m) => m.id !== item.id);

    mutate(
      (key) => isArray(key) && key[0] === SWR_ORDER_IMAGES_ENDPOINT,
      orderImageService
        .deleteOrderImagesAsync(orderPageStore.order?.id ?? 0, [item.id])
        .then(() => newImages),
      {
        optimisticData: newImages,
        rollbackOnError: true,
        populateCache: true,
        revalidate: false,
      }
    ).catch((e) => {
      notifyStore.showNotify('Не удалось удалить изображение', 'error');
      console.error(e);
    });
  };

  if (isLoading) {
    return (
      <Stack gap={4}>
        {times(3).map((i) => (
          <Box key={`${componentId}-loader-skeleton-${i}`}>
            <Skeleton variant='rounded' width={180} sx={{ mb: 2 }} />
            <Stack direction={'row'} gap={2} flexWrap={'wrap'}>
              <Skeleton variant='rounded' height={150} width={150} />
              <Skeleton variant='rounded' height={150} width={150} />
              <Skeleton variant='rounded' height={150} width={150} />
              <Skeleton variant='rounded' height={150} width={150} />
            </Stack>
          </Box>
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap={2}>
      {isMobile ? <UploadButton /> : <UploadDropZone />}
      <OrderImageList
        images={data ?? []}
        onImageDelete={intentionallyFloatingPromiseReturn(deleteImage)}
        onImageClick={(index) => {
          setIndex(index);
        }}
      />

      <OrderImageLightbox
        index={index}
        close={() => {
          setIndex(-1);
        }}
        slides={slides}
      />
    </Stack>
  );
});

export default ImageGallery;
